import React, { useEffect, useRef, useState } from "react";
import aboutImg from "../assets/img/about.jpg";

interface InnerBannerProps {
  statistics: {
    students: number;
    courses: number;
    teachers: number;
    awards: number;
  };
}

const AboutSection: React.FC<InnerBannerProps> = ({ statistics }) => {
  const [counters, setCounters] = useState({
    students: 0,
    courses: 0,
    teachers: 0,
    awards: 0,
  });

  const animatedCounters = useRef({
    students: Math.random() * statistics.students,
    courses: Math.random() * statistics.courses,
    teachers: Math.random() * statistics.teachers,
    awards: Math.random() * statistics.awards,
  });

  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const targetValues = statistics;
    const duration = 2000;
    const startTime = performance.now();

    const animateCounters = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const newValues = {
        students:
          animatedCounters.current.students +
          progress *
            (targetValues.students -
              animatedCounters.current.students),

        courses:
          animatedCounters.current.courses +
          progress *
            (targetValues.courses -
              animatedCounters.current.courses),

        teachers:
          animatedCounters.current.teachers +
          progress *
            (targetValues.teachers -
              animatedCounters.current.teachers),

        awards:
          animatedCounters.current.awards +
          progress *
            (targetValues.awards -
              animatedCounters.current.awards),
      };

      setCounters({
        students: Math.round(newValues.students),
        courses: Math.round(newValues.courses),
        teachers: Math.round(newValues.teachers),
        awards: Math.round(newValues.awards),
      });

      if (progress < 1) {
        animationRef.current =
          requestAnimationFrame(animateCounters);
      }
    };

    animationRef.current =
      requestAnimationFrame(animateCounters);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [statistics]);

  return (
    <section
      className="space-ptb"
      style={{
        position: "relative",
        overflow: "hidden",

        background:
          "linear-gradient(135deg, #020306 0%, #050912 50%, #020306 100%)",

        color: "#ffffff",

        borderBottom:
          "1px solid rgba(0,246,255,0.12)",

        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.025)",
      }}
    >
      {/* ======================================================
          BACKGROUND GRID
      ====================================================== */}

      <div
        style={{
          position: "absolute",
          inset: 0,

          pointerEvents: "none",

          opacity: 0.08,

          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",

          backgroundSize: "34px 34px",
        }}
      />

      {/* ======================================================
          CYAN GLOW
      ====================================================== */}

      <div
        style={{
          position: "absolute",

          width: "380px",
          height: "380px",

          left: "-200px",
          top: "-100px",

          borderRadius: "50%",

          background:
            "rgba(0,246,255,0.07)",

          filter: "blur(100px)",

          pointerEvents: "none",
        }}
      />

      {/* ======================================================
          PURPLE GLOW
      ====================================================== */}

      <div
        style={{
          position: "absolute",

          width: "350px",
          height: "350px",

          right: "-180px",
          bottom: "-150px",

          borderRadius: "50%",

          background:
            "rgba(139,92,255,0.07)",

          filter: "blur(100px)",

          pointerEvents: "none",
        }}
      />

      {/* ======================================================
          MAIN CONTENT
      ====================================================== */}

      <div
        className="container"
        style={{
          position: "relative",
          zIndex: 2,
        }}
      >
        <div className="row align-items-center">

          {/* ==================================================
              IMAGE
          ================================================== */}

          <div className="col-lg-6 mb-5 mb-lg-0">
            <div
              style={{
                position: "relative",

                padding: "8px",

                border:
                  "1px solid rgba(0,246,255,0.18)",

                background:
                  "rgba(0,246,255,0.025)",

                boxShadow:
                  "0 20px 60px rgba(0,0,0,0.55), 0 0 35px rgba(0,246,255,0.04)",
              }}
            >
              {/* Top cyan line */}

              <div
                style={{
                  position: "absolute",

                  top: "-1px",
                  left: "25px",

                  width: "90px",
                  height: "2px",

                  background:
                    "#00f6ff",

                  boxShadow:
                    "0 0 12px rgba(0,246,255,0.8)",

                  zIndex: 2,
                }}
              />

              <img
                className="img-fluid"
                src={aboutImg}
                alt="About Growniverse"
                style={{
                  display: "block",

                  width: "100%",

                  height: "auto",

                  filter:
                    "brightness(0.82) contrast(1.05)",

                  boxShadow:
                    "0 0 30px rgba(0,0,0,0.6)",
                }}
              />

              {/* Image overlay */}

              <div
                style={{
                  position: "absolute",

                  inset: 8,

                  background:
                    "linear-gradient(135deg, rgba(0,246,255,0.04), transparent 45%, rgba(139,92,255,0.04))",

                  pointerEvents: "none",
                }}
              />

              {/* Bottom scan line */}

              <div
                style={{
                  position: "absolute",

                  bottom: "7px",

                  left: "15%",
                  right: "15%",

                  height: "1px",

                  background:
                    "linear-gradient(90deg, transparent, rgba(0,246,255,0.45), transparent)",
                }}
              />
            </div>
          </div>

          {/* ==================================================
              ABOUT CONTENT
          ================================================== */}

          <div className="col-lg-6">
            <div
              style={{
                paddingLeft: "0px",
              }}
            >

              {/* Small Label */}

              <div
                style={{
                  display: "inline-flex",

                  alignItems: "center",

                  gap: "8px",

                  marginBottom: "14px",

                  padding:
                    "6px 12px",

                  border:
                    "1px solid rgba(0,246,255,0.18)",

                  borderRadius: "4px",

                  background:
                    "rgba(0,246,255,0.025)",

                  color: "#00f6ff",

                  fontSize: "9px",

                  fontWeight: 700,

                  letterSpacing: "2px",

                  textTransform:
                    "uppercase",
                }}
              >
                <span
                  style={{
                    width: "6px",
                    height: "6px",

                    borderRadius: "50%",

                    background:
                      "#00f6ff",

                    boxShadow:
                      "0 0 10px #00f6ff",
                  }}
                />

                GROWNIVERSE
              </div>

              {/* Heading */}

              <h2
                style={{
                  color: "#ffffff",

                  fontSize:
                    "clamp(30px, 4vw, 44px)",

                  fontWeight: 800,

                  lineHeight: 1.15,

                  marginBottom: "18px",

                  letterSpacing: "-1px",

                  textShadow:
                    "0 0 25px rgba(255,255,255,0.08)",
                }}
              >
                About Us
              </h2>

              {/* Cyan line */}

              <div
                style={{
                  width: "70px",

                  height: "2px",

                  marginBottom: "22px",

                  background:
                    "linear-gradient(90deg, #00f6ff, transparent)",

                  boxShadow:
                    "0 0 10px rgba(0,246,255,0.6)",
                }}
              />

              {/* Description */}

              <p
                style={{
                  color: "#aeb8c6",

                  fontSize: "15px",

                  lineHeight: 1.9,

                  marginBottom: "35px",

                  maxWidth: "650px",
                }}
              >
                Growniverse is your Digital Learning
                Platform Where you can learn new
                knowledge and skills without any
                physical distance barrier. You just
                not only get classes but you will get
                live webinars and specially Real Life
                Experience of Entrepreneurship.
              </p>

              {/* ==================================================
                  STATISTICS
              ================================================== */}

              <div className="row g-3">

                {/* STUDENTS */}

                <div className="col-6 col-sm-6">
                  <div
                    style={{
                      height: "100%",

                      padding: "18px 15px",

                      border:
                        "1px solid rgba(255,255,255,0.08)",

                      borderRadius: "8px",

                      background:
                        "rgba(255,255,255,0.018)",

                      boxShadow:
                        "inset 0 0 20px rgba(255,255,255,0.008)",

                      transition:
                        "all 0.25s ease",
                    }}
                  >
                    <div
                      style={{
                        color: "#00f6ff",

                        fontSize: "28px",

                        fontWeight: 800,

                        lineHeight: 1,

                        marginBottom: "8px",

                        textShadow:
                          "0 0 15px rgba(0,246,255,0.35)",
                      }}
                    >
                      {counters.students}
                    </div>

                    <div
                      style={{
                        color: "#8d9aaa",

                        fontSize: "11px",

                        fontWeight: 700,

                        letterSpacing: "1.2px",

                        textTransform:
                          "uppercase",
                      }}
                    >
                      Happy Students
                    </div>
                  </div>
                </div>

                {/* COURSES */}

                <div className="col-6 col-sm-6">
                  <div
                    style={{
                      height: "100%",

                      padding: "18px 15px",

                      border:
                        "1px solid rgba(255,255,255,0.08)",

                      borderRadius: "8px",

                      background:
                        "rgba(255,255,255,0.018)",
                    }}
                  >
                    <div
                      style={{
                        color: "#8b7cff",

                        fontSize: "28px",

                        fontWeight: 800,

                        lineHeight: 1,

                        marginBottom: "8px",

                        textShadow:
                          "0 0 15px rgba(139,92,255,0.35)",
                      }}
                    >
                      {counters.courses}+
                    </div>

                    <div
                      style={{
                        color: "#8d9aaa",

                        fontSize: "11px",

                        fontWeight: 700,

                        letterSpacing: "1.2px",

                        textTransform:
                          "uppercase",
                      }}
                    >
                      Our Courses
                    </div>
                  </div>
                </div>

                {/* TEACHERS */}

                <div className="col-6 col-sm-6">
                  <div
                    style={{
                      height: "100%",

                      padding: "18px 15px",

                      border:
                        "1px solid rgba(255,255,255,0.08)",

                      borderRadius: "8px",

                      background:
                        "rgba(255,255,255,0.018)",
                    }}
                  >
                    <div
                      style={{
                        color: "#00f6ff",

                        fontSize: "28px",

                        fontWeight: 800,

                        lineHeight: 1,

                        marginBottom: "8px",

                        textShadow:
                          "0 0 15px rgba(0,246,255,0.35)",
                      }}
                    >
                      {counters.teachers}
                    </div>

                    <div
                      style={{
                        color: "#8d9aaa",

                        fontSize: "11px",

                        fontWeight: 700,

                        letterSpacing: "1.2px",

                        textTransform:
                          "uppercase",
                      }}
                    >
                      Our Teachers
                    </div>
                  </div>
                </div>

                {/* AWARDS */}

                <div className="col-6 col-sm-6">
                  <div
                    style={{
                      height: "100%",

                      padding: "18px 15px",

                      border:
                        "1px solid rgba(255,255,255,0.08)",

                      borderRadius: "8px",

                      background:
                        "rgba(255,255,255,0.018)",
                    }}
                  >
                    <div
                      style={{
                        color: "#8b7cff",

                        fontSize: "28px",

                        fontWeight: 800,

                        lineHeight: 1,

                        marginBottom: "8px",

                        textShadow:
                          "0 0 15px rgba(139,92,255,0.35)",
                      }}
                    >
                      {counters.awards}
                    </div>

                    <div
                      style={{
                        color: "#8d9aaa",

                        fontSize: "11px",

                        fontWeight: 700,

                        letterSpacing: "1.2px",

                        textTransform:
                          "uppercase",
                      }}
                    >
                      Awards Won
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ======================================================
          BOTTOM SCAN LINE
      ====================================================== */}

      <div
        style={{
          position: "absolute",

          bottom: 0,

          left: "18%",
          right: "18%",

          height: "1px",

          background:
            "linear-gradient(90deg, transparent, rgba(0,246,255,0.35), transparent)",

          pointerEvents: "none",
        }}
      />
    </section>
  );
};

export default AboutSection;